import { useState, useEffect } from 'react';
import { Sparkles, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { useAISuggestions } from '@/lib/hooks/use-ai-suggestions';
import { useUserPreferences } from '@/lib/hooks/use-user-preferences';
import { cn } from '@/lib/utils';

interface AISuggestionsProps {
  conversationId: string | null;
  onSuggestionClick: (suggestion: string) => void;
  messageCount?: number; // Pass message count to trigger refetch
  totalMessageCount?: number; // Total number of messages in the conversation
}

export function AISuggestions({
  conversationId,
  onSuggestionClick,
  messageCount = 0,
  totalMessageCount = 0,
}: AISuggestionsProps) {
  const { preferences, updatePreferences } = useUserPreferences();
  const [isCollapsed, setIsCollapsed] = useState(preferences.suggestions_collapsed);

  // Sync with DB state
  useEffect(() => {
    setIsCollapsed(preferences.suggestions_collapsed);
  }, [preferences.suggestions_collapsed]);

  // Update DB when collapsed state changes
  const handleToggleCollapse = async (newState: boolean) => {
    setIsCollapsed(newState);
    try {
      await updatePreferences({ suggestions_collapsed: newState });
    } catch (error) {
      console.error('Failed to update suggestions collapsed state:', error);
      // Revert on error
      setIsCollapsed(!newState);
    }
  };

  // Only fetch suggestions if not collapsed
  const { suggestions, isLoading, isFetching } = useAISuggestions(
    conversationId,
    !!conversationId && !isCollapsed,
    messageCount
  );

  if (!conversationId) {
    return null;
  }

  // Hide if there are no messages in the chat
  if (totalMessageCount === 0) {
    return null;
  }

  if (isCollapsed) {
    return (
      <div className="p-2 border-t border-slate-200 bg-slate-50 flex items-center justify-center">
        <button
          onClick={() => handleToggleCollapse(false)}
          className="flex items-center gap-2 text-xs text-slate-500 hover:text-slate-700 transition"
        >
          <Sparkles className="w-3 h-3 text-[#4669ed]" />
          <span>Show AI Suggestions</span>
          <ChevronUp className="w-3 h-3" />
        </button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-4 border-t border-slate-200 bg-slate-50 flex items-center justify-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin text-[#4669ed]" />
        <span className="text-sm text-slate-600">
          Generating suggestions...
        </span>
      </div>
    );
  }

  if (suggestions.length === 0) {
    return null;
  }

  return (
    <div className="p-4 border-t border-slate-200 bg-slate-50">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#4669ed]" />
          <span className="text-sm font-semibold text-slate-700">
            AI Suggestions
          </span>
        </div>
        <div className="flex items-center gap-2">
          {isFetching && (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-[#4669ed]" />
          )}
          <button
            onClick={() => handleToggleCollapse(true)}
            className="p-1 rounded-lg transition hover:bg-slate-200"
            title="Collapse"
          >
            <ChevronDown className="w-4 h-4 text-slate-500" />
          </button>
        </div>
      </div>
      <div className={cn('flex flex-wrap gap-2', isFetching && 'opacity-50')}>
        {suggestions.slice(0, 2).map((suggestion, index) => (
          <button
            key={index}
            onClick={() => onSuggestionClick(suggestion)}
            className="text-sm px-4 py-2 rounded-full transition line-clamp-1 text-left border bg-white border-slate-200 hover:border-[#4669ed] hover:bg-[#4669ed]/5 text-slate-700"
            title={suggestion.length > 80 ? suggestion : undefined}
          >
            {suggestion.length > 80 ? suggestion.substring(0, 80) + '...' : suggestion}
          </button>
        ))}
      </div>
    </div>
  );
}
